/* eslint-disable react-hooks/preserve-manual-memoization */
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { Box, HStack, Heading, Radio, Text, Tooltip, VStack, useToast } from 'native-base'
import { ReactElement, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'

import { useMutation } from '@apollo/client'

import client from '../../apolloClient'
import HouseIconLightGreyBgSVG from '../../assets/HouseIconLightGreySVG'
import PencilIconLightGreySVG from '../../assets/PencilIconLightGreySVG'
import InfoIcon from '../../assets/info-black.svg'
import { AuthContext } from '../../context/AuthContext'
import {
  Address,
  AGREE_TO_TERMS_AND_CONDITIONS_CWA,
  UPDATE_ADDRESS,
  UpdateAddressMutation,
  UpdateAddressMutationVariables,
} from '@consumer/graphql'
import { createCanonicalContext, decorateCanonical, emitCanonical } from '@consumer/shared'
import { trackEvent } from '../../utils/analytics-v2'
import '../../utils/canonicalLog'
import { useScreenSize } from '../../utils/displayUtils'
import { logError } from '../../utils/monitorClient'
import { colors } from '../../utils/theme/configureTheme'
import ActionCard from '../ActionCard'
import { ModalContent } from '../Instructions/Common/ModalContent'
import { ToastAlert } from '../Instructions/Common/ToastAlert'
import { TermsAndConditionsFormType } from '../Instructions/TermAndConditions/TermsAndConditions'
import { IPermanentAddressUpdateModalContentProps } from './PermanentAddressUpdateModalContent'

interface IAddressOption {
  badgeText?: string
  titleText?: string
  descriptionText?: string
  key: string
  id: string
  value: string
  addressValue: Partial<Address> | 'addNewAddress'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: ReactElement<any> | null
  badgeColor?: string
  sectionTitle?: string
}

export const PermanentAddressUpdate = ({
  order,
  closeModal,
  toggleEditAddress,
  setOrder,
  handleSetAdjustGeopinToast,
  toggleMapModal,
  setUseUpdateNormalizedAddress,
  includeTermsAndConditions,
}: IPermanentAddressUpdateModalContentProps) => {
  const { t } = useTranslation()
  const { values, isValid } = useFormikContext<TermsAndConditionsFormType>()
  const { reloadUser } = useContext(AuthContext)

  const clientId = order?.clientId ?? undefined
  const addressUpdateOpIdRef = useRef<string>('')
  const addressUpdateStartMsRef = useRef<number>(0)
  const screenSize = useScreenSize()
  const isMobileScreen = isMobile || screenSize.width <= 650
  const toast = useToast()
  const addressSelections = useMemo<IAddressOption[]>(() => {
    const orderAddress = { ...order.address }
    const originalAddress = { ...order.originalAddress }
    return [
      {
        key: 'normalizedAddress',
        id: 'normalizedAddress',
        value: 'normalizedAddress',
        icon: <HouseIconLightGreyBgSVG />,
        badgeText: 'Veho Verified',
        badgeColor: 'info',
        titleText: `${
          orderAddress?.apartment && orderAddress?.apartment?.length > 0
            ? `${orderAddress.street} ${orderAddress.apartment}`
            : orderAddress.street
        }`,
        descriptionText: `${orderAddress.city}, ${orderAddress.state} ${orderAddress.zipCode}`,
        addressValue: { ...orderAddress },
        sectionTitle: 'Veho Verified Address',
      },
      {
        key: 'originalAddress',
        id: 'originalAddress',
        value: 'originalAddress',
        icon: <HouseIconLightGreyBgSVG />,
        badgeText: 'Original Address',
        badgeColor: 'lightWarn',
        titleText: `${
          originalAddress?.apartment && originalAddress?.apartment?.length > 0
            ? `${originalAddress.street} ${originalAddress.apartment}`
            : originalAddress.street
        }`,
        descriptionText: `${originalAddress.city}, ${originalAddress.state} ${originalAddress.zipCode}`,
        addressValue: { ...originalAddress },
        sectionTitle: 'Address Entered at Checkout',
      },
      {
        key: 'addNewAddress',
        id: 'addNewAddress',
        value: 'addNewAddress',
        addressValue: 'addNewAddress',
        icon: <PencilIconLightGreySVG />,
        titleText: 'Add a new address',
        sectionTitle: 'Need to send this to a different address?',
      },
    ]
  }, [order.address, order.originalAddress])
  const [addressSelection, setAddressSelection] = useState<IAddressOption | null>(null)

  // updateAddress mutation is used to update consumerSpecifiedAddressNormalization on the normalized address
  // to ensure address update modal isn't shown again
  const [updateAddress, { loading: updateAddressLoading }] = useMutation<
    UpdateAddressMutation,
    UpdateAddressMutationVariables
  >(UPDATE_ADDRESS, {
    errorPolicy: 'all',
    client: client,
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (data?.updateAddress) {
        const opId = addressUpdateOpIdRef.current
        if (opId) {
          decorateCanonical(opId, { mutationDurationMs: Date.now() - addressUpdateStartMsRef.current })
          emitCanonical(opId, 'success')
          addressUpdateOpIdRef.current = ''
        }
        // only update consumerSpecifiedAddressNormalization as the rest of the address is unchanged
        setOrder({ ...order, address: { ...order.address, consumerSpecifiedAddressNormalization: true } })

        trackEvent('pau_verified_address_select_success', {
          features: 'permanent_address_update',
          veho_client_id: clientId,
        })

        closeModal({
          clearAddressFields: false,
          clearGeocode: false,
          showToast: true,
          toastProps: {
            placement: 'top',
            duration: 3000,
            render: ({ id }: { id: string }) => (
              <ToastAlert
                id={id}
                title={'Your address has been updated!'}
                description={''}
                status={'success'}
                variant={'subtle'}
                isClosable={false}
                closeToast={toast.close}
                toastAlertProps={{
                  style: {
                    position: 'absolute',
                    top: 10,
                    zIndex: 8000,
                  },
                }}
              />
            ),
          },
        })
      }
    },
    onError: error => {
      const opId = addressUpdateOpIdRef.current
      if (opId) {
        decorateCanonical(opId, {
          mutationDurationMs: Date.now() - addressUpdateStartMsRef.current,
          error: error?.message ?? 'unknown',
        })
        emitCanonical(opId, 'failure')
        addressUpdateOpIdRef.current = ''
      }
      logError('updateAddress error', { error })

      trackEvent('pau_verified_address_select_error', {
        features: 'permanent_address_update',
        error_reason: 'unknown',
        veho_client_id: clientId,
      })

      closeModal({
        clearAddressFields: false,
        clearGeocode: false,
        showToast: true,
        toastProps: {
          duration: 3000,
          placement: 'top',
          render: ({ id }) => (
            <ToastAlert
              id={id}
              title={'There was an error updating your address'}
              description={'Please try again or contact support'}
              status={'error'}
              variant={'subtle'}
              isClosable={false}
              closeToast={toast.close}
              toastAlertProps={{
                style: {
                  position: 'absolute',
                  top: 10,
                  zIndex: 8000,
                },
              }}
            />
          ),
        },
      })
    },
  })
  const [agreedToTermsAndConditions] = useMutation(AGREE_TO_TERMS_AND_CONDITIONS_CWA, {
    client: client,
    onCompleted: reloadUser,
  })

  const getModalWidth = useMemo(() => {
    if (isMobileScreen) {
      return '85%'
    }
    if (screenSize.width <= 775) {
      return '60%'
    }
    if (screenSize.width <= 950) {
      return '50%'
    }
    if (screenSize.width <= 1100) {
      return '45%'
    }
    return '35%'
  }, [isMobileScreen, screenSize.width])

  const handleCloseModalError = useCallback(() => {
    // bail out if no normalized address id
    closeModal({
      clearAddressFields: false,
      clearGeocode: false,
      showToast: true,
      toastProps: {
        duration: 3000,
        placement: 'top',
        render: ({ id }) => (
          <ToastAlert
            id={id}
            title={'There was an error updating your address'}
            description={'Please try again or contact support'}
            status={'error'}
            variant={'subtle'}
            isClosable={false}
            closeToast={toast.close}
            toastAlertProps={{
              style: {
                position: 'absolute',
                top: 10,
                zIndex: 8000,
              },
            }}
          />
        ),
      },
    })
    return
  }, [closeModal, toast])

  const handleSavePermanentAddress = useCallback(async () => {
    if (includeTermsAndConditions && values.acceptedTerms) {
      await agreedToTermsAndConditions()
    }

    if (!addressSelection) {
      toast.show({
        duration: 3000,
        placement: 'top',
        render: ({ id }) => (
          <ToastAlert
            id={id}
            title={'Please make a selection and try again'}
            description={''}
            status={'error'}
            variant={'subtle'}
            isClosable={false}
            closeToast={toast.close}
            toastAlertProps={{
              style: {
                position: 'absolute',
                top: 10,
                zIndex: 8000,
              },
            }}
          />
        ),
      })
      return
    }

    try {
      if (addressSelection?.value === 'addNewAddress') {
        toggleEditAddress({
          clearAddressFields: true,
          locationUpdate: null,
          useLocationUpdate: false,
          toggleMap: false,
        })
        return
      }

      if (order?.address?.normalizedAddressId) {
        if (addressSelection?.value === 'normalizedAddress') {
          // selecting the normalized address indicates that we normalized the address correctly,
          // so we need just need to update the consumerSpecifiedAddressNormalization property on the address
          // to avoid showing the permanent address update modal to the user again
          if (order?.address?.addressId && order?.consumerId) {
            const opId = crypto.randomUUID()
            addressUpdateOpIdRef.current = opId
            addressUpdateStartMsRef.current = Date.now()
            createCanonicalContext('CanonicalAddressUpdate', opId, { platform: 'web' })
            updateAddress({
              variables: {
                addressId: order?.address?.addressId,
                addressInput: {
                  consumerSpecifiedAddressNormalization: true,
                },
                clientId: 'veho',
                consumerId: order?.consumerId,
              },
            })
          } else {
            handleCloseModalError()
            return
          }
        }

        if (addressSelection?.value === 'originalAddress') {
          // selecting original address indicates that we normalized the address incorrectly,
          // so we need to update the address to the original address that came in with the order
          closeModal({
            clearAddressFields: false,
            clearGeocode: false,
            showToast: false,
          })
          toggleMapModal({ showToastAfterClose: false })
          handleSetAdjustGeopinToast({
            headerText: 'Confirm the pin is in the correct delivery location for your address',
            bodyText: '',
          })
          setUseUpdateNormalizedAddress(true)
        }
      } else {
        // bail out if no normalized address id
        handleCloseModalError()
        return
      }
    } catch (error) {
      logError('handleSavePermanentAddress error', { error })
      handleCloseModalError()
    }
  }, [
    addressSelection,
    toggleEditAddress,
    toast,
    updateAddress,
    order?.address,
    order?.consumerId,
    handleCloseModalError,
    toggleMapModal,
    handleSetAdjustGeopinToast,
    closeModal,
    setUseUpdateNormalizedAddress,
    includeTermsAndConditions,
    values,
    agreedToTermsAndConditions,
  ])

  const handleRadioSelectOnChange = (value: string) => {
    setAddressSelection(addressSelections.find(selection => selection.value === value) ?? null)
  }

  const disableSubmit = !addressSelection || (includeTermsAndConditions && !isValid)

  return (
    <ModalContent
      headerMain="Update Delivery Address"
      headerSecondary="Update your future Veho delivery addresses by selecting an option below. We won't ask you this again."
      headerSecondaryStyles={{ fontSize: 12, fontWeight: 400, lineHeight: 14, letterSpacing: 0 }}
      useAddInstructionsLaterButton={false}
      submitText={'Confirm'}
      submitButtonStyles={{ flex: 1, width: '100%' }}
      cancelText={t('common:CANCEL')}
      cancelButtonStyles={{ backgroundColor: colors.brand.black, color: colors.brand.white, flex: 1, width: '100%' }}
      modalStyles={{
        width: getModalWidth,
      }}
      onSubmit={handleSavePermanentAddress}
      closeModal={closeModal}
      disabled={disableSubmit}
      loading={updateAddressLoading}
      includeTermsAndConditions={includeTermsAndConditions}
      termsAndConditionsAction={'updating my address'}
      headerMainChildren={
        <HStack alignItems={'center'}>
          <Heading textAlign={'center'} fontSize={20} fontWeight={500} letterSpacing={'-2%'}>
            Update Delivery Address
          </Heading>
          <Box marginLeft={1}>
            <Tooltip
              label="Veho only delivers to certain zip codes. If you have trouble changing your address, or if your address isn't in our service area, contact the original merchant"
              placement="bottom"
              width="232px"
              backgroundColor={colors.brand.softBlack}
            >
              <img src={InfoIcon} alt="information" width="22px" height="22px" />
            </Tooltip>
          </Box>
        </HStack>
      }
    >
      <VStack width="100%">
        <Radio.Group
          name="updateAddress"
          accessibilityLabel="selection for updating delivery address"
          defaultValue=""
          onChange={value => handleRadioSelectOnChange(value)}
        >
          {addressSelections.map(selection => {
            return (
              <VStack px={isMobileScreen ? 0 : 2} alignItems="stretch" width="100%">
                <Text fontSize={14} fontWeight={500} lineHeight={15} mb={2}>
                  {selection.sectionTitle}
                </Text>
                <ActionCard
                  key={selection.key}
                  titleText={selection.titleText}
                  titleTextStyles={{ fontSize: 16, fontWeight: 400, letterSpacing: '-1%' }}
                  badgeText={selection?.badgeText}
                  descriptionText={selection?.descriptionText}
                  descriptionTextStyles={{ fontSize: 16, fontWeight: 400, letterSpacing: '-1%' }}
                  cardAction="radioSelect"
                  CardIcon={selection.icon}
                  radioValue={selection.value}
                  cardStyleProps={{
                    width: '100%',
                    marginBottom: 2,
                    borderRadius: 8,
                    paddingHorizontal: '16px',
                    paddingVertical: '16px',
                    borderWidth: 1,
                  }}
                  isSelected={addressSelection?.value === selection.value}
                  selectedBorderColor={colors.brand.selectedBlue}
                  selectedBackgroundColor={colors.brand.white}
                  badgeColor={selection?.badgeColor}
                />
              </VStack>
            )
          })}
        </Radio.Group>
      </VStack>
    </ModalContent>
  )
}
